import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import { isLocalApp } from 'academy-common-lib/utils/url';
import { isTrackCardModel, isTrackOrLessonCardModel, isShortCardModel } from 'academy-common-lib/features/cards/utils';

/**
 * Fully-qualified base URL of Academy Studio for the current portal and
 * environment (e.g. https://app.hubspot.com/academy-studio/:portalId).
 *
 * Mirrors `getAcademyBaseUrl` in `utils/url.ts`: resolves to the `local`
 * subdomain during local development so Studio deep links work off localhost.
 */
export const getStudioBaseUrl = () => `${getFullUrl(isLocalApp() ? 'local' : 'app')}/academy-studio/${PortalIdParser.get() || 'l'}`;

/**
 * Builds the relative Academy Studio path for a content card, dispatching on
 * the card's type, e.g. `/builder/lesson/:id/edit/en`.
 *
 * The Studio route shape is hard-coded here with no compile-time link to
 * Studio's `AppRoutes.tsx`; keeping the dispatch in this single function is the
 * whole defensive strategy (see the file's role in the plan's risk section).
 *
 * Edge cases to handle:
 * - shorts (`SHORT_FORM_CONTENT`) key on `contentId`, NOT `id` — Studio's
 *   `on-the-go/:contentId` route expects the short's distinct content id
 * - `TRACK` maps to Studio's `builder/course` path (Academy "track" ==
 *   Studio "course"); `LESSON` maps to `builder/lesson`
 * - `{language}` is a required, lowercase trailing segment
 * - any type without an editable Studio record (playlists, learning paths,
 *   live events, or an unhandled enum value) must return `null` so the caller
 *   omits the action rather than linking to a dead Studio URL
 *
 * @param item the content card to link into Studio
 * @return the relative Studio path (with leading slash), or `null` if the card
 *   type has no editable Studio record
 */
export const getStudioContentPath = item => {
  if (isShortCardModel(item)) {
    return `/on-the-go/${item.contentId}/edit/${item.language.toString().toLowerCase()}`;
  }
  if (isTrackOrLessonCardModel(item)) {
    const segment = isTrackCardModel(item) ? 'builder/course' : 'builder/lesson';
    return `/${segment}/${item.id}/edit/${item.language.toString().toLowerCase()}`;
  }
  return null;
};

/**
 * Builds the fully-qualified Academy Studio edit URL for a content card, e.g.
 * `https://app.hubspot.com/academy-studio/:portalId/builder/lesson/:id/edit/en`.
 *
 * Returns `null` for a card that has no editable Studio record, so callers
 * never turn the "Open in Studio" action into a dead link to the Studio root.
 *
 * @param item the content card to link into Studio
 * @return the fully-qualified Studio URL, or `null` if unsupported
 */
export const getStudioContentUrl = item => {
  const path = getStudioContentPath(item);
  return path === null ? null : `${getStudioBaseUrl()}${path}`;
};